import React, { useEffect, useContext, createContext, useRef } from 'react';

type DataFetchTask = Promise<unknown>;
type SkipInitialClientFetchRef = React.MutableRefObject<boolean>;

interface DataFetchingContextType {
  registerTask: (task: DataFetchTask) => void;
  isServer: boolean;
  isCollecting: boolean;
  skipInitialClientFetchRef: SkipInitialClientFetchRef;
}

const DataFetchingContext = createContext<DataFetchingContextType>({
  registerTask: () => {},
  isServer: typeof window === 'undefined',
  isCollecting: typeof window === 'undefined',
  skipInitialClientFetchRef: { current: false },
});

interface DataFetchingProviderProps {
  children: React.ReactNode;
  onAllTasksComplete?: () => void;
  serverTaskRegistrar?: (task: DataFetchTask) => void;
  isCollecting?: boolean;
  skipInitialClientFetch?: boolean;
}

export const DataFetchingProvider: React.FC<DataFetchingProviderProps> = ({
  children,
  onAllTasksComplete,
  serverTaskRegistrar,
  isCollecting: isCollectingProp,
  skipInitialClientFetch = false,
}) => {
  const tasks = useRef<DataFetchTask[]>([]);
  const isServer = typeof window === 'undefined';
  const isCollecting = isCollectingProp !== undefined ? isCollectingProp : isServer;
  const skipInitialClientFetchRef = useRef(skipInitialClientFetch && !isServer);

  useEffect(() => {
    if (skipInitialClientFetchRef.current) {
      skipInitialClientFetchRef.current = false;
    }
  }, []);

  const registerTask = (task: DataFetchTask) => {
    if (!task) return;

    tasks.current.push(task);

    if (isServer && serverTaskRegistrar) {
      serverTaskRegistrar(task);
    }

    if (!isServer && onAllTasksComplete) {
      Promise.all(tasks.current).then(onAllTasksComplete);
    }
  };

  return (
    <DataFetchingContext.Provider
      value={{ registerTask, isServer, isCollecting, skipInitialClientFetchRef }}
    >
      {children}
    </DataFetchingContext.Provider>
  );
};

export const useDataFetching = () => useContext(DataFetchingContext);

interface DataFetchProps {
  [key: string]: unknown;
}

interface DataFetchOptions {
  onUpdate?: boolean;
  deps?: (props: DataFetchProps) => unknown[];
}

export function withDataFetching<P extends DataFetchProps>(
  fetchData: (props: P) => DataFetchTask | undefined,
  options: DataFetchOptions = {}
) {
  return (Component: React.ComponentType<P>) => {
    const WithDataFetching = (props: P) => {
      const { registerTask, isServer, isCollecting, skipInitialClientFetchRef } = useDataFetching();
      const hasInitialized = useRef(false);

      if (isServer && isCollecting) {
        const task = fetchData(props);
        if (task) {
          registerTask(task);
        }
      }

      const depsArray = options.deps ? options.deps(props) : options.onUpdate ? [props] : [];

      useEffect(() => {
        if (isServer) return;

        if (!hasInitialized.current) {
          if (skipInitialClientFetchRef.current) {
            hasInitialized.current = true;
            return;
          }

          const task = fetchData(props);
          if (task) {
            registerTask(task);
          }
          hasInitialized.current = true;
          return;
        }

        if (options.onUpdate) {
          const task = fetchData(props);
          if (task) {
            registerTask(task);
          }
        } else if (options.deps) {
          /* deps changed after mount — refetch (e.g. SPA query / path change) */
          const task = fetchData(props);
          if (task) {
            registerTask(task);
          }
        }
      }, depsArray); // eslint-disable-line react-hooks/exhaustive-deps

      return <Component {...props} />;
    };

    WithDataFetching.displayName = `WithDataFetching(${
      Component.displayName || Component.name || 'Component'
    })`;
    return WithDataFetching;
  };
}
